import Image from 'next/image';
import clsx from 'clsx';
import { useTranslation } from '@whoop/i18n/lang/client';
import { useShallow } from 'zustand/react/shallow';
import { getFormatters } from 'ui/hooks/formatter';
import { cltw } from 'ui/utils/index';
import { usePathname } from 'next/navigation';
import LandingPageSubtextV2 from '@/components/landingPage/landingPageSubtext/landingPageSubtextV2';
import { useSiteStore } from '@/store/SiteStoreProvider';
import type { GenericItem } from '@/client/contentfulClient';
import { getLandingPageSubtextCopyV2 } from '@/components/landingPage/LandingPageContent/landingPageContentUtils';
import { TruemedHsaFsaBadge } from '@/components/TruemedHsaFsaBadge/TruemedHsaFsaBadge';
import { ADVANCED_LABS_RAF_SOURCE } from '@/lib/constants/promos';
import { featureFlagNames } from '@/client/featureFlagClient';
import AdvancedLabsLandingPageSubtext from '@/components/landingPage/landingPageSubtext/AdvancedLabsLandingPageSubtext';
const imageContainerVariants = {
  join: 'gap-8',
  upgrade: 'bg-gray-50'
};
const imageVariants = {
  join: 'px-6 max-w-[476px] w-full',
  upgrade: 'min-w-[399px] px-6 pb-6'
};
const descriptionVariants = {
  join: 'w-80 md:w-auto md:max-w-[40rem]',
  upgrade: 'w-85 md:w-auto md:max-w-[30rem]'
};
interface JoinNextGenerationSectionProps {
  content: GenericItem;
  isTrialMembershipAvailable?: boolean;
}
export default function JoinNextGenerationSection({
  content,
  isTrialMembershipAvailable = false
}: JoinNextGenerationSectionProps) {
  const {
    t
  } = useTranslation('membershipTiers');
  const isUpgradeFlow = usePathname().includes('upgrade');
  const variant = isUpgradeFlow ? 'upgrade' : 'join';
  const {
    currency,
    language,
    region,
    country,
    promoContent,
    promoInfo,
    isRafGiftFlow,
    featureFlags
  } = useSiteStore(useShallow(state => ({
    currency: state.currency,
    language: state.language,
    region: state.region,
    country: state.country,
    promoContent: state.promoContent,
    promoInfo: state.promoInfo,
    isRafGiftFlow: state.isRafGiftFlow,
    featureFlags: state.featureFlags
  })));
  const {
    formatDisplayPrice
  } = getFormatters({
    currency,
    language,
    region
  });
  const getDescriptionContent = () => {
    // Upgrade flow
    if (isUpgradeFlow) {
      return content.description;
    } else {
      // Join flow
      if (!isTrialMembershipAvailable) return '';
      const getStartedForContent = t('getStartedFor', {
        formattedPrice: formatDisplayPrice(0)
      });
      if (country === 'US') {
        return <div className='flex flex-col md:flex-row'>
            {!promoContent?.partnership_name && !promoInfo?.promo_code && <>
                {getStartedForContent}
                <span className='mx-1 hidden md:block'>|</span>
              </>}
            <TruemedHsaFsaBadge />
          </div>;
      }
      return getStartedForContent;
    }
  };
  const isRafAL = promoInfo?.referral?.code.source === ADVANCED_LABS_RAF_SOURCE;
  const showAdvancedLabsCopy = featureFlags[featureFlagNames.RAF_AL_MESSAGING] && isRafAL;
  const descriptionContent = getDescriptionContent();
  const landingPageSubtextCopy = getLandingPageSubtextCopyV2({
    promoContent,
    promoInfo,
    currency,
    language,
    region,
    showAdvancedLabsCopy: showAdvancedLabsCopy,
    isRafGiftFlow
  });
  return <>
      <div className='flex flex-col text-center'>
        <div className={cltw('flex flex-col gap-5 pt-9 md:gap-8 md:pt-16', imageContainerVariants[variant])}>
          <h1 className='px-4 text-4xl font-semibold md:text-6xl'>
            {isRafGiftFlow ? t('giftTheNextGeneration') : content.title}
          </h1>
          {content.secondaryImage ? <div className='flex h-[322px] min-w-[240px] justify-center'>
              <Image alt={content.secondaryImage?.title ?? ''} className='relative min-w-[240px]' height={18} priority src={content.secondaryImage?.url ?? ''} width={209} />
            </div> : null}
          <div className='flex justify-center overflow-hidden'>
            <Image alt={content.image?.title ?? ''} className={clsx('relative', imageVariants[variant])} height={186} priority src={content.image?.url ?? ''} width={476} data-sentry-element="Image" data-sentry-source-file="JoinNextGenerationSection.tsx" />
          </div>
        </div>
        {isRafGiftFlow ? null : <div className='flex flex-col items-center p-7 md:p-8'>
            <p className={clsx('text-base', descriptionVariants[variant])} id='style-override-body-xl'>
              {content.subtitle}
            </p>
            {descriptionContent ? <div className={clsx('text-base', descriptionVariants[variant])} id='style-override-body-xl'>
                {descriptionContent}
              </div> : null}
          </div>}
      </div>
      <div className={cltw(isRafGiftFlow ? 'pt-6' : '')}>
        {landingPageSubtextCopy && !isUpgradeFlow ? <LandingPageSubtextV2 desktopSubtext={landingPageSubtextCopy.desktopSubtext} imageSrc={landingPageSubtextCopy.imageSrc} mobileSubtext={landingPageSubtextCopy.mobileSubtext} secondLine={landingPageSubtextCopy.secondLine} showAdvancedLabsCopy={showAdvancedLabsCopy} /> : null}
      </div>
    </>;
}